import {
  OktonLoadingCard,
  OktonErrorCard,
  OktonPageHeader,
  OktonSearchFilter,
  OktonDataGrid,
  OktonEmptyState,
  OktonConfirmDialog,
  ColunaGrid,
  useGrid,
  TAMANHOS_PAGINA,
} from '@okton/platform-ui';
import {
  Badge,
  Box,
  Button,
  Flex,
  IconButton,
  useColorModeValue,
  useToast,
} from '@chakra-ui/react';
import { Building2, Coins, Pencil, Plus, Trash2 } from 'lucide-react';
import React, { useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import BankAppPage from '../../layout/BankAppPage';
import {
  ContaBancaria,
  identificacaoAgencia,
  useContasBancarias,
  useTiposConta,
} from '../../services/hooks/useContasBancarias';
import { useDeleteContaBancaria } from '../../services/hooks/useContasBancariasMutations';
import { colunasDataGrid } from '../../utils/colunasDataGrid';
import formatValue from '../../utils/formatValue';

const ContasBancarias = () => {
  const history = useHistory();
  const bg = useColorModeValue('white', 'gray.800');

  const {
    data: accounts = [],
    isLoading,
    isError,
    error,
    refetch,
  } = useContasBancarias();
  const { data: tiposConta = [] } = useTiposConta();
  const deleteMutation = useDeleteContaBancaria();
  const toast = useToast();
  const [itemExcluir, setItemExcluir] = useState<string | null>(null);
  const [excluindo, setExcluindo] = useState<string | null>(null);

  const remove = async () => {
    if (!itemExcluir) return;
    setExcluindo(itemExcluir);
    try {
      await deleteMutation.mutateAsync(itemExcluir);
      setItemExcluir(null);
      refetch();
    } catch (err) {
      toast({
        title: err instanceof Error ? err.message : 'Erro ao remover a conta.',
        status: 'error',
        position: 'bottom-right',
        isClosable: true,
      });
    } finally {
      setExcluindo(null);
    }
  };

  const nomeTipo = (tipo: string) =>
    tiposConta.find(t => String(t.valor) === String(tipo))?.descricao ?? '—';

  const [busca, setBusca] = useState('');
  // O nome do Tipo vem de outro hook, então a coluna precisa dele em mãos: sem
  // isso a busca por "Conta Corrente" não acharia nada.
  const colunas = useMemo<ColunaGrid<ContaBancaria>[]>(
    () => [
      {
        chave: 'descricao_cts',
        titulo: 'Descrição',
        valor: c => c.descricao_cts,
      },
      { chave: 'nome_ban', titulo: 'Banco', valor: c => c.nome_ban },
      {
        chave: 'agencia_agc',
        titulo: 'Agência',
        valor: c =>
          c.agencia_agc
            ? identificacaoAgencia(c.agencia_agc, c.digito_agc, c.posto_agc)
            : '',
      },
      {
        chave: 'numero_cts',
        titulo: 'Conta',
        valor: c => `${c.numero_cts}${c.digito_cts ? `-${c.digito_cts}` : ''}`,
      },
      {
        chave: 'tipo_cts',
        titulo: 'Tipo',
        valor: c =>
          tiposConta.find(t => String(t.valor) === String(c.tipo_cts))
            ?.descricao ?? '',
      },
      { chave: 'nome_moe', titulo: 'Moeda', valor: c => c.nome_moe },
      {
        chave: 'saldoinicial_cts',
        titulo: 'Saldo inicial',
        valor: c => Number(c.saldoinicial_cts),
        tipo: 'numero',
        alinhamento: 'right',
      },
    ],
    [tiposConta],
  );

  const {
    ordenacao,
    alternarOrdenacao,
    itens: visiveis,
    filtrados,
    pagina,
    setPagina,
    tamanhoPagina,
    setTamanhoPagina,
  } = useGrid(accounts, colunas, busca);

  return (
    <BankAppPage>
      <OktonPageHeader
        eyebrow="CADASTROS"
        title="Contas Bancárias"
        subtitle="Gerencie as contas para conciliação"
        actions={
          <>
            <Flex ml="auto" my="3" align="center" gridGap={2} flexWrap="wrap">
              <OktonSearchFilter
                value={busca}
                onChange={setBusca}
                placeholder="Buscar por descrição, banco, conta..."
              />
              <Flex
                borderWidth="1px"
                p="2"
                rounded="2xl"
                bg={bg}
                align="center"
              >
                <Button
                  leftIcon={<Plus size={18} />}
                  colorScheme="blue"
                  onClick={() => history.push('/contas-bancarias/nova')}
                >
                  Nova Conta
                </Button>
              </Flex>
            </Flex>
          </>
        }
      />

      {isLoading ? (
        <Flex mt="1">
          <OktonLoadingCard h="200px" />
        </Flex>
      ) : isError ? (
        <Flex mt="1">
          <OktonErrorCard
            h="200px"
            description={
              error instanceof Error && error.message
                ? error.message
                : 'Tente novamente. Se o problema persistir, contate o suporte.'
            }
            onRetry={refetch}
          />
        </Flex>
      ) : (
        <Box bg={bg} mt="1" rounded="2xl">
          {filtrados.length === 0 ? (
            <OktonEmptyState
              icon={Building2}
              title={
                busca
                  ? 'Nada encontrado para a busca'
                  : 'Nenhuma conta bancária cadastrada'
              }
            />
          ) : (
            <OktonDataGrid
              rows={visiveis}
              columns={[
                ...colunasDataGrid(colunas, ordenacao, alternarOrdenacao, {
                  descricao_cts: acc => (
                    <Box fontWeight="bold">{acc.descricao_cts}</Box>
                  ),
                  tipo_cts: acc => nomeTipo(acc.tipo_cts),
                  nome_moe: acc =>
                    acc.nome_moe ? (
                      <Badge colorScheme="blue" borderRadius="full" px="2">
                        {acc.nome_moe}
                      </Badge>
                    ) : (
                      '—'
                    ),
                  saldoinicial_cts: acc => (
                    <Box fontWeight="semibold" whiteSpace="nowrap">
                      {formatValue(Number(acc.saldoinicial_cts))}
                    </Box>
                  ),
                }),
                {
                  key: 'acoes',
                  header: null,
                  align: 'right',
                  render: acc => (
                    <Flex justify="flex-end" gridGap="1">
                      <IconButton
                        aria-label="Moedas da Conta"
                        title="Moedas da Conta"
                        size="sm"
                        variant="ghost"
                        colorScheme="purple"
                        icon={<Coins size={16} />}
                        onClick={() =>
                          history.push(`/contas-bancarias/${acc.id_cts}/moedas`)
                        }
                      />
                      <IconButton
                        aria-label="Editar"
                        size="sm"
                        variant="ghost"
                        colorScheme="blue"
                        icon={<Pencil size={16} />}
                        onClick={() =>
                          history.push(`/contas-bancarias/${acc.id_cts}`)
                        }
                      />
                      <IconButton
                        aria-label="Excluir"
                        size="sm"
                        variant="ghost"
                        colorScheme="red"
                        icon={<Trash2 size={16} />}
                        isLoading={excluindo === acc.id_cts}
                        onClick={() => setItemExcluir(acc.id_cts)}
                      />
                    </Flex>
                  ),
                },
              ]}
              getRowId={acc => acc.id_cts}
              page={pagina + 1}
              pageSize={tamanhoPagina}
              total={filtrados.length}
              pageSizeOptions={TAMANHOS_PAGINA}
              onPageChange={nova => setPagina(nova - 1)}
              onPageSizeChange={setTamanhoPagina}
            />
          )}
        </Box>
      )}
      <OktonConfirmDialog
        isOpen={Boolean(itemExcluir)}
        onClose={() => {
          if (!excluindo) setItemExcluir(null);
        }}
        onConfirm={remove}
        title="Confirmar exclusão"
        description="Deseja excluir esta conta bancária?"
        confirmLabel="Excluir"
        intent="danger"
        isLoading={Boolean(excluindo)}
      />
    </BankAppPage>
  );
};

export default ContasBancarias;
